import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';
import {
  Form,
  minLength,
  PasswordInput,
  SaveButton,
  useNotify,
  useTranslate,
} from 'react-admin';
import AvatarInput from '../../components/AvatarInput';
import { useUser } from '../../providers/userProvider';
import { apiUrl, httpClient, sendAsFormData } from '../../utils/httpUtils';
import { validatePasswords } from '../members/MembersCreate';

export const Profile = () => {
  const { userData, loading: userLoading } = useUser();
  const [isSaveDisabled, setIsSaveDisabled] = useState(false);
  const [formKey, setFormKey] = useState(0);
  const t = useTranslate();
  const notify = useNotify();

  const handleSubmitNewPassword = async data => {
    try {
      const url = `${apiUrl}/profile/change-password`;
      const res = await httpClient(url, {
        method: 'POST',
        body: JSON.stringify(data),
      });
      if (res.status === 200) {
        notify(t('resources.profile.messageUpdatePasswordSuccess'), {
          type: 'success',
        });
        setFormKey(prevKey => prevKey + 1);
      } else {
        throw new Error('Error en la solicitud');
      }
    } catch (error) {
      notify(t('resources.profile.messageUpdatePasswordError'), {
        type: 'error',
      });
    }
  };

  const handleAvatarUpload = async data => {
    try {
      const file = data.rawFile;
      const params = { data: { avatar_file: file } };
      const response = await sendAsFormData(
        'POST',
        'profile/update-avatar',
        params
      );
      const newAvatarUrl = response.data.response.avatar;
      let auth = localStorage.getItem('auth');
      auth = JSON.parse(auth);
      const updatedUserData = { ...auth, avatar: newAvatarUrl };
      localStorage.setItem('auth', JSON.stringify(updatedUserData));

      notify(t('resources.profile.messageUpdateAvatarSuccess'), {
        type: 'success',
      });
      setIsSaveDisabled(true);
    } catch (error) {
      console.error('Error:', error);
      notify(t('resources.profile.messageUpdateAvatarError'), {
        type: 'error',
      });
    }
  };

  const concatenatedRoles = userData?.roles?.map(role => role).join(', ');

  if (userLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        marginTop: '70px',
        gap: '36px',
      }}
    >
      <Typography color="textPrimary" variant="h5" sx={{ mb: 2 }}>
        {t('resources.profile.title')}
      </Typography>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          gap: '24px',
        }}
      >
        <Card>
          <CardContent>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-around',
              }}
            >
              <Box
                sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}
              >
                <Typography sx={{ fontWeight: 600, fontSize: '26px' }}>
                  {userData?.username}
                </Typography>
                <Box flex={1} mt={'-0.5em'}>
                  <Typography>{concatenatedRoles}</Typography>
                  <Typography>{userData?.email}</Typography>
                </Box>
              </Box>
              <Form
                onSubmit={async data => {
                  await handleAvatarUpload(data.avatar_file);
                }}
              >
                <Box
                  display={{ xs: 'block', sm: 'flex' }}
                  sx={{ alignItems: 'center', justifyContent: 'center' }}
                >
                  <Box
                    sx={{
                      width: 'fit-content',
                      height: 'fit-content',
                      marginBottom: '-20px',
                    }}
                  >
                    <Box flex={1} style={{ alignSelf: 'center' }}>
                      <AvatarInput
                        resource="members"
                        source="avatar"
                        sizeMax={70}
                        initialAvatar={userData?.avatar}
                      />
                    </Box>
                  </Box>
                </Box>
                <Grid
                  container
                  direction="row"
                  justifyContent="flex-end"
                  alignItems="flex-end"
                >
                  <SaveButton
                    disabled={isSaveDisabled}
                    sx={{
                      backgroundColor: '#3F49DB',
                      borderRadius: '500px !important',
                      textTransform: 'none !important',
                      fontSize: '14px !important',
                      lineHeight: '19px !important',
                      fontWeight: '600 !important',
                      letterSpacing: '0.5px !important',
                      padding: '8px 32px!important',
                      boxShadow: 'none !important',
                      svg: {
                        display: 'none',
                      },
                    }}
                  />
                </Grid>
              </Form>
            </Box>
          </CardContent>
        </Card>

        <Card>
          <CardContent
            sx={{
              padding: '24px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <Typography color="textPrimary" variant="h5" sx={{ mb: 2 }}>
              {t('resources.profile.updatePasswordTitle')}
            </Typography>
            <Form
              key={formKey}
              validate={validatePasswords}
              onSubmit={handleSubmitNewPassword}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Box flex={1} mr={{ xs: 0, sm: '0.5em' }} width={'100%'}>
                  <PasswordInput
                    resource="members"
                    fullWidth
                    validate={minLength(6)}
                    label="Contraseña actual"
                    name="old_password"
                  />
                </Box>
                <Box display={{ xs: 'block', sm: 'flex' }}>
                  <Box flex={1} mr={{ xs: 0, sm: '0.5em' }}>
                    <PasswordInput
                      source="password"
                      resource="members"
                      fullWidth
                      validate={minLength(6)}
                      label="Nueva Contraseña"
                      name="password"
                    />
                  </Box>
                  <Box flex={1} ml={{ xs: 0, sm: '0.5em' }}>
                    <PasswordInput
                      label="Confirmar contraseña"
                      source="password_confirmation"
                      resource="members"
                      validate={minLength(6)}
                      fullWidth
                      name="password_confirmation"
                    />
                  </Box>
                </Box>
              </Box>
              <Grid
                container
                direction="row"
                justifyContent="flex-end"
                alignItems="flex-end"
              >
                <SaveButton
                  sx={{
                    backgroundColor: '#3F49DB',
                    borderRadius: '500px !important',
                    textTransform: 'none !important',
                    fontSize: '14px !important',
                    lineHeight: '19px !important',
                    fontWeight: '600 !important',
                    letterSpacing: '0.5px !important',
                    padding: '8px 32px!important',
                    boxShadow: 'none !important',
                    svg: {
                      display: 'none',
                    },
                  }}
                />
              </Grid>
            </Form>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};
